import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChartLine, Package, FileText, TrendUp, Eye, Clock } from '@phosphor-icons/react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [topParts, setTopParts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard`);
      setStats(response.data.stats);
      setRecentAnalyses(response.data.recent_analyses || []);
      setTopParts(response.data.top_detected_parts || []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const statCards = [
    { label: 'Analyses effectuées', value: stats?.total_analyses || 0, icon: ChartLine, color: '#E63946' },
    { label: 'Analyses terminées', value: stats?.completed_analyses || 0, icon: Eye, color: '#2A9D8F' },
    { label: 'Demandes de devis', value: stats?.total_quotes || 0, icon: FileText, color: '#E63946' },
    { label: 'Taux de conversion', value: `${stats?.conversion_rate || 0}%`, icon: TrendUp, color: '#2A9D8F' }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const styles = { pending: 'bg-amber-100 text-amber-700', analyzing: 'bg-blue-100 text-blue-700', completed: 'bg-[#2A9D8F]/10 text-[#2A9D8F]', error: 'bg-[#E63946]/10 text-[#E63946]' };
    const labels = { pending: 'En attente', analyzing: 'En cours', completed: 'Terminée', error: 'Erreur' };
    return <span className={`px-2 py-1 text-xs font-bold uppercase ${styles[status] || styles.pending}`}>{labels[status] || status}</span>;
  };

  if (loading) return <AdminLayout title="Dashboard"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-white border border-gray-200 p-6 shadow-sm" data-testid={`stat-${i}`}>
              <div className="flex items-start justify-between">
                <div><p className="text-gray-500 text-sm mb-2">{stat.label}</p><p className="font-['Outfit'] text-3xl font-bold text-gray-900">{stat.value}</p></div>
                <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}><Icon size={24} style={{ color: stat.color }} /></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-['Outfit'] text-lg font-bold text-gray-900">Dernières analyses</h2>
              <Link to="/admin/analyses" className="text-[#E63946] text-sm font-medium hover:underline">Voir tout</Link>
            </div>
            <div className="divide-y divide-gray-100">
              {recentAnalyses.length > 0 ? recentAnalyses.slice(0, 5).map((a, i) => (
                <div key={i} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex-1"><p className="text-gray-900 font-medium">{a.brand || 'Véhicule'} {a.model || ''}</p><p className="text-gray-400 text-sm flex items-center gap-2"><Clock size={14} />{formatDate(a.created_at)}</p></div>
                  <div className="flex items-center gap-4"><span className="text-gray-500 text-sm">{a.detected_parts?.length || 0} pièces</span>{getStatusBadge(a.status)}</div>
                </div>
              )) : <div className="p-8 text-center text-gray-400">Aucune analyse récente</div>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200"><h2 className="font-['Outfit'] text-lg font-bold text-gray-900 flex items-center gap-2"><Package size={20} className="text-[#E63946]" />Pièces les plus détectées</h2></div>
            <div className="p-4 space-y-3">
              {topParts.length > 0 ? topParts.map((part, i) => (<div key={i} className="flex items-center justify-between"><span className="text-gray-600 text-sm truncate flex-1">{part._id}</span><span className="text-[#2A9D8F] font-bold text-sm ml-2">{part.count}</span></div>)) : <p className="text-gray-400 text-center py-4">Aucune donnée</p>}
            </div>
          </div>
          <div className="bg-white border border-gray-200 shadow-sm mt-6">
            <div className="p-6 border-b border-gray-200"><h2 className="font-['Outfit'] text-lg font-bold text-gray-900">Actions rapides</h2></div>
            <div className="p-4 space-y-2">
              <Link to="/admin/pieces" className="block w-full py-3 px-4 bg-[#E63946]/10 text-[#E63946] text-sm font-medium hover:bg-[#E63946]/20">+ Ajouter une pièce</Link>
              <Link to="/admin/demandes" className="block w-full py-3 px-4 bg-gray-50 text-gray-700 text-sm font-medium hover:bg-gray-100">Voir les demandes ({stats?.new_quotes || 0} nouvelles)</Link>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
